import { CSSProperties, ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../context/ThemeContext';
import Button from './Button';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Click outside / Escape to dismiss. Defaults to true. */
  dismissable?: boolean;
  /** Backdrop opacity 0..1. */
  backdropOpacity?: number;
  /** Wraps children in a styled card. Set false to render children raw. */
  card?: boolean;
  contentStyle?: CSSProperties;
}

export default function Modal({
  open,
  onClose,
  children,
  dismissable = true,
  backdropOpacity = 0.6,
  card = true,
  contentStyle,
}: ModalProps) {
  const { colors } = useTheme();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (dismissable && e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, dismissable, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      onClick={dismissable ? onClose : undefined}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: `rgba(0,0,0,${backdropOpacity})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 9000,
        animation: 'aga-fade-in 160ms ease-out',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={
          card
            ? {
                backgroundColor: colors.modalBg,
                border: `1px solid ${colors.cardBorder}`,
                borderRadius: 20,
                padding: 24,
                width: '100%',
                maxWidth: 440,
                maxHeight: 'calc(100dvh - 32px)',
                overflowY: 'auto',
                color: colors.text,
                animation: 'aga-modal-in 200ms cubic-bezier(0.22, 1, 0.36, 1)',
                ...contentStyle,
              }
            : { width: '100%', maxWidth: 440, ...contentStyle }
        }
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

interface AlertButton {
  text: string;
  onPress?: () => void;
  style?: 'default' | 'cancel' | 'destructive';
}

interface AlertModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  message?: string;
  buttons?: AlertButton[];
}

export function AlertModal({ open, onClose, title, message, buttons }: AlertModalProps) {
  const { colors } = useTheme();
  const btns: AlertButton[] = buttons && buttons.length > 0 ? buttons : [{ text: 'OK', style: 'default' }];

  return (
    <Modal open={open} onClose={onClose}>
      <div style={{ fontSize: 18, fontWeight: 800, color: colors.text, marginBottom: 8 }}>{title}</div>
      {message && (
        <div style={{ fontSize: 14, color: colors.textSub, lineHeight: 1.5, marginBottom: 20 }}>{message}</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {btns.map((b, i) => (
          <Button
            key={i}
            label={b.text}
            variant={b.style === 'destructive' ? 'danger' : b.style === 'cancel' ? 'ghost' : 'primary'}
            onClick={() => {
              b.onPress?.();
              onClose();
            }}
          />
        ))}
      </div>
    </Modal>
  );
}
